/**
 * OpportunityFormSidebar - Sidebar para criação de novas negociações.
 * 
 * Orquestra os campos do formulário de criação de negociação,
 * delegando lógica ao hook useOpportunityForm.
 * 
 * Princípios aplicados:
 * - Componentização: Usa subcomponentes (SidebarHeader, CurrencyInput)
 * - Single Responsibility: Apenas criação de negociação
 * - Separation of Concerns: Lógica delegada ao hook
 */

import React from 'react';
import { PlusIcon } from '../../../components/icons';
import { ComboboxSelector } from '../../../components/inputs/ComboboxSelector';
import { CrmUser, Contact } from '../../../types';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import ContactSelect from './ContactSelect';
import { useOpportunityForm } from '../hooks/useOpportunityForm';
import { useOrigins } from '../../settings/origins/hooks/useOrigins';
import Input from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { SidebarHeader } from './SidebarHeader';
import { CurrencyInput } from './CurrencyInput';
import { usePermissions } from '../../../hooks/usePermissions';
import { cn } from '@/lib/utils';

interface OpportunityFormSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Deve retornar Promise<boolean> indicando se o save concluiu com sucesso.
     * Em falha, a sidebar permanece aberta com os dados preservados.
     */
    onSave: (opportunityData: any) => Promise<boolean> | boolean;
    onOpenContactModal: () => void;
    contacts: Contact[];
    users: CrmUser[];
    createdContact?: Contact | null;
    onCreatedContactApplied?: () => void;
}

const OpportunityFormSidebar: React.FC<OpportunityFormSidebarProps> = ({
    isOpen,
    onClose,
    onSave,
    onOpenContactModal,
    contacts,
    users,
    createdContact,
    onCreatedContactApplied
}) => {
    const { funnelStages } = useLeads();
    const { origins, loading: originsLoading, createOrigin } = useOrigins();
    const { canAssignLeads } = usePermissions();

    const {
        formData,
        handleSelectContact,
        handleClearContact,
        handleValueChange,
        handleChange,
        handleSubmit,
        isSubmitting
    } = useOpportunityForm({
        isOpen,
        funnelStages,
        onSave,
        onClose,
        createdContact,
        onCreatedContactApplied
    });

    const canSubmit = !!formData.contactId?.trim() && !!formData.source?.trim() && !!formData.title?.trim();

    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div
                className={`fixed inset-0 overlay-premium transition-opacity duration-300 z-40 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed inset-y-0 right-0 z-50 flex h-full w-full transform flex-col bg-surface-0 shadow-lg transition-transform duration-[var(--slow,250ms)] ease-[cubic-bezier(.4,0,.2,1)]',
                    'md:w-[500px]',
                    isOpen ? 'translate-x-0' : 'translate-x-full',
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby="sidebar-title"
            >
                <div className="flex h-full flex-col">
                    <SidebarHeader
                        title="Nova Negociação"
                        subtitle="Preencha os dados da negociação"
                        onClose={onClose}
                    />

                    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div className="min-h-0 flex-1 space-y-s5 overflow-y-auto px-s4 py-s6 md:px-s6">
                            {/* Etapa */}
                            <Select
                                label="Etapa"
                                name="stage"
                                value={formData.stage}
                                onChange={handleChange}
                                variant="default"
                                placeholder="Selecione uma etapa"
                                options={funnelStages.map(stage => ({
                                    value: stage.id,
                                    label: stage.name
                                }))}
                                required
                            />

                            {/* Contato */}
                            <div>
                                <label className="block text-sm font-medium text-default mb-2">
                                    Contato
                                </label>
                                <ContactSelect
                                    contacts={contacts}
                                    selectedContactId={formData.contactId}
                                    onSelect={handleSelectContact}
                                    onClear={handleClearContact}
                                    onAddContact={onOpenContactModal}
                                    pinnedContact={createdContact}
                                />
                            </div>

                            {/* Origem */}
                            <div>
                                <label className="block text-sm font-medium text-default mb-2">
                                    Origem
                                </label>
                                <ComboboxSelector
                                    value={formData.source}
                                    onChange={(val) => handleChange({ target: { name: 'source', value: val } } as any)}
                                    options={origins}
                                    loading={originsLoading}
                                    placeholder="Selecione ou crie uma origem..."
                                    createLabel="Criar origem"
                                    onCreate={async (name) => {
                                        const newOrigin = await createOrigin({ name: name.trim() });
                                        return newOrigin?.name || name.trim();
                                    }}
                                />
                            </div>

                            {/* Usuário Responsável — só owner/admin pode atribuir terceiros; membros ficam só com visão própria no Kanban */}
                            {canAssignLeads ? (
                                <Select
                                    label="Usuário Responsável"
                                    name="assignedTo"
                                    value={formData.assignedTo}
                                    onChange={handleChange}
                                    variant="default"
                                    placeholder="Selecione um usuário"
                                    options={users.map(user => ({
                                        value: user.id,
                                        label: user.name
                                    }))}
                                />
                            ) : null}

                            {/* Título da Negociação */}
                            <Input
                                label="Título da Negociação"
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Ex: Projeto de Marketing Digital"
                                variant="default"
                                required
                                helperText="Preencha este campo."
                            />

                            {/* Valor da Negociação */}
                            <CurrencyInput
                                label="Valor da Negociação"
                                name="value"
                                value={formData.value}
                                onChange={handleValueChange}
                            />
                        </div>

                        <footer className="border-t border-border bg-surface-subtle px-s4 py-s4 md:px-s6">
                            <Button
                                type="submit"
                                variant="default"
                                size="lg"
                                className="min-h-11 w-full gap-s2 touch-manipulation"
                                disabled={isSubmitting || !canSubmit}
                            >
                                <PlusIcon className="size-5" aria-hidden />
                                {isSubmitting ? 'Salvando...' : 'Criar Negociação'}
                            </Button>
                            {!formData.contactId && (
                                <p className="mt-s2 text-center text-xs text-muted">
                                    Selecione ou crie um contato salvo para continuar.
                                </p>
                            )}
                        </footer>
                    </form>
                </div>
            </aside>
        </>
    );
};

export default OpportunityFormSidebar;
